/*
***************************************
Utility functions 
***************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking
import { favIconLight16, favIconLight32, favIconDark16, favIconDark32 } from "./images.js";

// prettier-ignore
export { 
  consoleMsg, 
  setMenu, 
  setInspector,
  isVisible, 
  resetTextValue, 
  updateOfflineStatus, 
  selectRadios,
  populateTable,
  getStoredValue,
  attachRefresherEvent,
  handleRefresh,
  waitForContent,
  updateFavicon,
  titleCase,
  isLiveData,
  logDataMode
};

// Make sure our app references are using the global app object associated with window
const app = window.app;

async function loadHTML(url, content) {
	try {
		const response = await fetch(url);
		const html = await response.text();
		content = html;
		return content; // Return the content to use directly
	} catch (error) {
		console.error("Error fetching HTML content:", error);
		return ""; // Return empty string in case of error
	}
}

/*
SINGLE SOURCE OF TRUTH for live vs. dummy data.

Live-ness used to be read straight off app.settings.liveData at five call sites with
four different polarities, and that flag defaults to false in globals.js and is only
flipped true late by NAVS index.php (from a DOMContentLoaded handler). Every failure
mode — flag late, missing, or reset — therefore read as "dummy", i.e. the app failed
OPEN into test behaviour while running against real NAVS data.

This requires two independent facts to agree:
  1. app.settings.liveData === true          (what NAVS explicitly told us)
  2. app.data.navsData.apiLink is non-empty  (a real submission endpoint exists)

index.php injects a real apiLink; both dummy JSON fixtures ship "apiLink": null. So
(2) is a fact about the data rather than a flag anyone can forget to set. If the two
ever disagree we are in a broken state, so we report dummy and logDataMode() shouts
about it — never submit to a live endpoint on a half-initialised app.
*/
function isLiveData() {
	return app.settings.liveData === true && Boolean(app.data?.navsData?.apiLink);
}

/*
Diagnostic for the above. Logs the resolved mode plus both raw inputs so a real NAVS
session shows which branch was taken and why. Disagreement between the two inputs is
a console.warn, because it means something is initialised wrong.
*/
function logDataMode(where) {
	const flag = app.settings.liveData;
	const apiLink = app.data?.navsData?.apiLink;
	const live = isLiveData();
	consoleMsg(`[DATA MODE] ${where}: ${live ? "LIVE" : "DUMMY"} (liveData=${flag}, apiLink=${apiLink || "none"}, readyState=${document.readyState})`);

	if ((flag === true) !== Boolean(apiLink)) {
		console.warn(`[DATA MODE] ${where}: MISMATCH — liveData=${flag} but apiLink=${apiLink || "none"}. Treating as DUMMY.`);
	}
	return live;
}

// Define function for toggling console messages
function consoleMsg(...args) {
	if (app.settings.isDevelopment === true) {
		console.log(...args);
		return true;
	}
}

// Build the navigation menu from the current flow (single source of truth:
// PageFlowManager). Flow items come from generateMenuHTML(); the static
// "Application Settings" item is prepended. Replaces the old per-vendor /
// per-direction menu templates.
function setMenu() {
	const menu = document.getElementById("menu");
	if (!menu) return;

	menu.innerHTML = `
	<ion-menu content-id="main-content">
		<ion-header>
			<ion-toolbar>
				<ion-title>Navigation</ion-title>
			</ion-toolbar>
		</ion-header>
		<ion-content>
			<ion-list>
				<ion-menu-toggle auto-hide="false">
					<ion-router-link href="#/app-settings">
						<ion-item>
							<ion-label color="primary">Application Settings</ion-label>
						</ion-item>
					</ion-router-link>
				</ion-menu-toggle>
				${app.flowManager.generateMenuHTML()}
			</ion-list>
		</ion-content>
	</ion-menu>`;
}

// Sets inspector name in app title bar (not currently in use with Melissa's redesign on 08/09/25)
function setInspector(inspector) {
	const inspectorNameElement = document.getElementById("inspector-name");
	if (inspectorNameElement) {
		inspectorNameElement.innerHTML = inspector;
	} else {
		console.error("Element with ID 'inspector-name' not found in the template.");
	}
}

/*
Fills the read-only cells of an Inspection Details table with NAVS-passed data.

Rows are declared statically in details.html; each read-only value cell carries a
data-field attribute holding the exact NAVS key. This iterates the DOM rather than the
data, so editable rows (Starting Mileage, License Plate Number) have no [data-field]
cell and are never touched here — their ion-input owns its own value.
*/
function populateTable(tableId, dataObject) {
	const table = document.querySelector(`#${tableId}`);

	if (!table) {
		console.error(`Table with ID '${tableId}' not found.`);
		return;
	}

	for (const valueCell of table.querySelectorAll("td.inspectionValue[data-field]")) {
		const value = dataObject[valueCell.dataset.field];
		valueCell.textContent = value ?? "";

		// Match previous behavior: a field NAVS didn't supply produced no row at all
		valueCell.closest("tr").hidden = value === undefined || value === null || value === "";
	}
}

/*
Reads one field out of stored page data by its id.

handleSubmit keys each page's stored entries by a running index, not by field name, so a
fixed position silently returns the wrong field as soon as a page gains or reorders one —
page 1 gained License Plate Number directly below Starting Mileage. Always look up by id.
*/
function getStoredValue(pageData, pageKey, id) {
	return Object.values(pageData?.[pageKey] ?? {}).find((entry) => entry.id === id)?.value ?? "";
}

function isVisible(element) {
	return !!element.offsetParent;
}

// Reset text area value (AKA [formerly] alert2)

/* Function that displays alert for confirmation to reset (delete) value of associated text area.  If canceled,
 associated ion-radio should be reset.  */

function resetTextValue(dialog, textId, divId) {
	const divElement = document.getElementById(divId);
	const photos = document.querySelectorAll("#" + divId + " img");
	const textArea = document.getElementById(textId);
	if (dialog) {
		// pick one of the three messages already embedded on the alert,
		// but fall back to the static template message if a dataset value is missing
		const defaultMessage = dialog.getAttribute("message") || dialog.message || "";

		if (photos.length > 0 && textArea.value) {
			dialog.message = dialog.dataset.msgTextAndPhoto || defaultMessage;
		} else if (photos.length > 0) {
			dialog.message = dialog.dataset.msgPhotoOnly || defaultMessage;
		} else {
			dialog.message = dialog.dataset.msgTextOnly || defaultMessage;
		}

		dialog.present(dialog);
		consoleMsg("Alert data = ", dialog.data);
		dialog.data = { textId: textId, divId: divId, textArea: textArea };
	}
}

// Get last key stored in Ionic Storage
async function getLastKey() {
	const keys = await store.keys(); // Get all keys from storage
	if (keys.length > 0) {
		const lastKey = keys[keys.length - 1]; // Get the last key
		consoleMsg("Last key:", lastKey);
		return lastKey;
	} else {
		consoleMsg("No keys found in storage.");
		return null;
	}
}

function removeDate(ionDate, div) {
	consoleMsg("Date alert OK clicked");
	ionDate.value = undefined;
	hideDiv(div);
}

/* Function to check one checkmark from each group so don't manually need to check them.*/

function selectRadios(nodeList) {
	if (app.settings.checkRadios) {
		for (const node of nodeList) {
			// Seed only: never overwrite an answer already present (restored or user-set)
			if (node.value) continue;

			node.value = "OK";

			if (app.state.currentPage === "page10") {
				node.value = "Hold";
			}
		}
	}
}

function selectFile(uploadDiv) {
	let input = document.createElement("input");
	document.getElementById("bannerImg").style.display = "block";
	input.type = "file";
	input.accept = ".jpeg,.jpg,.png";
	input.onchange = (_) => {
		// you can use this method to get file and perform respective operations
		let files = Array.from(input.files);
		if (files && files[0]) {
			var reader = new FileReader();

			reader.onload = function (e) {
				document.getElementById("bannerImg").src = e.target.result;
				consoleMsg("Saved image");
			};
		}

		reader.readAsDataURL(input.files[0]);
		//consoleMsg(files);
	};
	input.click();
}

function localStorageSpace() {
	var allStrings = "";
	for (var key in window.localStorage) {
		if (window.localStorage.hasOwnProperty(key)) {
			allStrings += window.localStorage[key];
		}
	}
	return allStrings ? 3 + (allStrings.length * 16) / (8 * 1024) + " KB" : "Empty (0 KB)";
}

function showDialog(id) {
	let dialog = document.getElementById(id);
	dialog.show();
}

function hideDialog(id, val) {
	consoleMsg("Dialog value = " + val);
	document.getElementById(id).hide();
	return val;
}

/* 08/01/24: New function added to check Internet connection */
const checkOnlineStatus = async () => {
	if (!navigator.onLine) {
		return false; // Device is not connected to a network
	}
	try {
		const online = await fetch("https://jsonplaceholder.typicode.com/todos/1", {
			cache: "no-store",
		});
		return online.status >= 200 && online.status < 300; // either true or false
	} catch (err) {
		return false; // definitely offline
	}
};

const updateOfflineStatus = async (elem) => {
	const myDiv = document.getElementById("offline-div");
	const ionContent = typeof elem === "string" ? document.getElementById(elem) : elem;
	const refresher = ionContent?.querySelector?.("ion-refresher") || document.querySelector("ion-refresher");

	let result = false;
	try {
		result = await checkOnlineStatus();
	} catch (err) {
		console.warn("checkOnlineStatus() failed; treating as offline.", err);
	}

	// myDiv is optional; bail if absent
	if (!myDiv) {
		console.warn("updateOfflineStatus: #offline-div not found.");
		return;
	}

	// Toggle hidden/offline-visible safely
	myDiv.classList.toggle("hidden", !!result);

	if (ionContent) {
		ionContent.classList.toggle("offline-visible", !result);
	} else {
		//console.warn("updateOfflineStatus: ionContent not found for elem:", elem);
	}

	if (refresher) {
		refresher.classList.toggle("offline-visible", !result);
	} else {
		// common: some pages do not include an ion-refresher
		// console.debug to avoid noise if expected
		console.debug("updateOfflineStatus: no ion-refresher in this view (ok).");
	}
};

/* NOTE: handleRefresh function was previously defined in classes.js., and was called from
my page initialization script, which also was in classes.js and used this.querySelector syntax
to reference the refresher.  This broke when moving both scripts externally.  I initially fixed
by using document.querySelector, but this will only work if there is a single ion-refresher
on any given page of my SPA.  This is currently the case, but if I ever move to implementing
some sort of dashboard-like interface, there may be a use case for multiple refreshers for
different parts of the dashboard: for example, to refresh data content in a page block.

Given this potential use case, I'm introducing the following new 
function, which can be passed this as its argument from within individual page class
definitions.
*/

function attachRefresherEvent(container) {
	customElements.whenDefined("ion-refresher").then(() => {
		const refresher = container.querySelector("ion-refresher");
		refresher?.addEventListener("ionRefresh", handleRefresh);
	});
}

function handleRefresh(event) {
	consoleMsg("Pull-to-refresh triggered");

	// Simulate refresh logic (e.g., update the offline status or reload data)
	setTimeout(() => {
		consoleMsg("Refresh complete");
		event.target.complete(); // Stop the refresher spinner
		setTimeout(() => {
			window.location.reload();
		}, 300);
	}, 1000);
}

// Define constant function to ensure loadHTML content has finished loading before attempting to access it
async function waitForContent(key) {
	let attempts = 0;
	while (!app.content[key]) {
		if (attempts % 10 === 0) {
			consoleMsg(`Waiting for content: ${key}...`);
		}
		await new Promise((resolve) => setTimeout(resolve, 50));
		attempts++;
	}
	consoleMsg(`✅ Content ready: ${key}`);
	return app.content[key];
}

// Function to update the favicon
function updateFavicon() {
	const fav16 = document.getElementById("fav-16");
	const fav32 = document.getElementById("fav-32");

	// Guard: skip if favicon elements don't exist (e.g., in test environment)
	if (!fav16 || !fav32) {
		return;
	}

	const darkModeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

	if (darkModeMediaQuery.matches) {
		// User prefers dark mode
		fav16.href = favIconDark16;
		fav32.href = favIconDark32;
		consoleMsg("FAV ICON UPDATED TO DARK...");
	} else {
		// User prefers light mode
		fav16.href = favIconLight16;
		fav32.href = favIconLight32;
		consoleMsg("FAV ICON UPDATED TO LIGHT...");
	}
}

/* 
********************************************************
Archive: legacy functions not currently in use
********************************************************
*/

// Legacy function for injecting footer element (header and footer are now in index.html)
async function setFooter(name) {
	let templateContent = "",
		myFooter = "templates/footer-template.html";
	await loadHTML(myFooter, templateContent)
		.then((content) => {
			templateContent = content;
		})
		.then(() => {
			const myTemplate = document.querySelector(`[name="${name}"]`);
			consoleMsg("TEMPLATE = ", myTemplate);
			myTemplate.innerHTML = templateContent;
		});
}

// Legacy function for injecting header content
async function setHeader(name, inspector) {
	let templateContent = "",
		myHeader = "templates/header-template.html";
	await loadHTML(myHeader, templateContent)
		.then((content) => {
			templateContent = content;
		})
		.then(() => {});
	const myTemplate = document.getElementById(name);
	consoleMsg("TEMPLATE = ", myTemplate);
	myTemplate.innerHTML = templateContent;
	const userNameElement = myTemplate.querySelector("#inspector-name");
	if (userNameElement) {
		consoleMsg("User name element = ", userNameElement);
		/* This wasn't working using document.getElementById - robot suggested this approach (see ChatGPT log
		'DOM Manipulation Fix)*/
		userNameElement.innerHTML = inspector;
	} else {
		console.error("Element with ID 'inspector-name' not found in the template.");
	}
}

// Legacy function for dynamically injecting scripts: no longer in use
async function loadScript(scr) {
	let script = document.createElement("script");
	script.type = "text/javascript";
	script.src = scr;
	let myScript = document.querySelector(`script[src="${scr}"]`);
	if (myScript) {
		consoleMsg("Script already loaded...");
		consoleMsg("Form = ", form);
	} else {
		document.head.appendChild(script);
		consoleMsg("Loading " + script.src);
	}
}

async function unloadScript(scr) {
	let script,
		myHead = document.head;
	script = document.querySelector("script[src=" + CSS.escape(scr) + "]");
	myHead.removeChild(script);
	consoleMsg("Removed script ", src);
}

function getBase64Image(img) {
	var canvas = document.createElement("canvas");
	canvas.width = img.width;
	canvas.height = img.height;

	var ctx = canvas.getContext("2d");
	ctx.drawImage(img, 0, 0);

	var dataURL = canvas.toDataURL("image/png");

	return dataURL.replace(/^data:image\/(png|jpg);base64,/, "");
}

async function getImgRatio(img) {
	setTimeout(async () => {
		const imgRatio = img.naturalHeight / img.naturalWidth;
		consoleMsg("Ratio = ", imgRatio);
		return imgRatio;
	}, 100);
}

function getText() {
	fetch("page1.html").then((response) => {
		consoleMsg("Fetched text:");
		consoleMsg(response);
	});
}

function titleCase(s) {
	return s
		.toLowerCase()
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

/*function consoleMsg(type, ...args) {
	if (app.settings.isDevelopment === true) {
		const styles = {
			debug: "color: blue; background-color: lightgray; font-weight: bold;",
			info: "color: green; background-color: lightgray;",
			warn: "color: orange;",
			error: "color: red; background-color: lightgray; font-weight: bold;",
		};

		if (styles[type?.toLowerCase()]) {
			// Apply styling for recognized types
			console.log(`%c${type}:`, styles[type.toLowerCase()], ...args);
		} else if (args.length > 0) {
			// No recognized type, but there are args → log normally without extra prefix
			console.log(...[type, ...args].filter((arg) => arg !== undefined));
		}
	}
}*/
